import {MyCheckFieldChange, ProposalChangeStatus} from './mycheck-diff';

/**
 * Entscheidung je Schlüssel einer Regel-Map (Prüfparameter `parameterValues`, Tags `tagValues`) bei
 * einem chatBIC-Vorschlag (#2789). Die Diff-Module liefern für eine Regel nur den Pfad
 * `configJson.parameterValues` bzw. `configJson.tagValues` als Ganzes; dieser Pfad bleibt die Sammelmarke
 * (wie bei `RowDecisions`), und hier wird festgehalten, welche Schlüssel darin noch offen sind und welche
 * der Nutzer einzeln übernommen hat. Reine Logik, kein Angular.
 */

export type RuleMapField = 'parameterValues' | 'tagValues';

export interface MapEntryChange extends MyCheckFieldChange {
  status: ProposalChangeStatus;
}

/**
 * Eintrag des Regel-Details, den der Vorschlag auf Set-Ebene löscht (`setRemovedRuleEntries` im KI- und
 * Hybrid-Editor): ein Prüfparameter bzw. Tag, dessen Set-Zeile gelöscht wird, während der Regelwert stehen bleibt.
 */
export interface SetRemovedRuleEntry {
  key: string;
  name: string;
  value: string;
  change: MapEntryChange;
  /** Die zu löschende Set-Zeile, an deren Entscheidung ✓/✗ im Regel-Detail gekoppelt sind. */
  removal: { before: { id?: string; name?: string }; index: number };
}

function isPresent(value: string | undefined): value is string {
  return value !== undefined && value !== null && value !== '';
}

/**
 * Leere Werte (`''` / `undefined`) gelten als „nicht vorhanden“. Nur vorher → removed, nur nachher →
 * added, beide nicht leer und verschieden → changed. Gleiche bzw. leer-gleiche Einträge fehlen in der Map.
 * Reihenfolge: zuerst die Schlüssel von `after`, dann die nur in `before`.
 */
export function diffMapEntries(before: Record<string, string> | undefined,
                               after: Record<string, string> | undefined): Map<string, MapEntryChange> {
  const result = new Map<string, MapEntryChange>();
  const keys = new Set([...Object.keys(after ?? {}), ...Object.keys(before ?? {})]);
  for (const key of keys) {
    const b = before?.[key];
    const a = after?.[key];
    const hasBefore = isPresent(b);
    const hasAfter = isPresent(a);
    if (!hasBefore && hasAfter) {
      result.set(key, {before: b, after: a, status: 'added'});
    } else if (hasBefore && !hasAfter) {
      result.set(key, {before: b, after: a, status: 'removed'});
    } else if (hasBefore && hasAfter && b !== a) {
      result.set(key, {before: b, after: a, status: 'changed'});
    }
  }
  return result;
}

interface EntryState {
  open: string[];
  accepted: string[];
  /** Status je Schlüssel beim Anwenden des Vorschlags (siehe {@link RuleEntryDecisions.initialStatus}). */
  initial: Map<string, ProposalChangeStatus>;
}

/** Offene und übernommene Map-Einträge je (Regel, Feld) eines Vorschlags. */
export class RuleEntryDecisions {
  private readonly states = new Map<string, EntryState>();

  /** Legt die offenen Schlüssel fest (beim Anwenden des Vorschlags) und liefert ihre Anzahl. */
  init(ruleId: string, field: RuleMapField,
       before: Record<string, string> | undefined, after: Record<string, string> | undefined): number {
    const entries = diffMapEntries(before, after);
    const open = [...entries.keys()];
    const id = this.id(ruleId, field);
    if (open.length === 0) {
      this.states.delete(id);
    } else {
      const initial = new Map([...entries].map(([key, change]) => [key, change.status] as const));
      this.states.set(id, {open, accepted: [], initial});
    }
    return open.length;
  }

  /** Offene Schlüssel in Einfügereihenfolge. */
  openKeys(ruleId: string, field: RuleMapField): string[] {
    return [...(this.states.get(this.id(ruleId, field))?.open ?? [])];
  }

  /**
   * Status eines offenen Schlüssels beim Anwenden des Vorschlags, `undefined` wenn er nicht (mehr) offen ist.
   * Er bleibt fest, auch wenn der Nutzer den Wert danach ändert: Ein geänderter oder neuer Wert, den der
   * Nutzer leert, um selbst etwas einzutragen, wird dadurch nicht zu „Wird gelöscht“, und ein zu löschender
   * Eintrag bleibt schreibgeschützt.
   */
  initialStatus(ruleId: string, field: RuleMapField, key: string): ProposalChangeStatus | undefined {
    const state = this.states.get(this.id(ruleId, field));
    return state?.open.includes(key) ? state.initial.get(key) : undefined;
  }

  isOpen(ruleId: string, field: RuleMapField, key: string): boolean {
    return this.states.get(this.id(ruleId, field))?.open.includes(key) ?? false;
  }

  /** Schließt einen offenen Schlüssel. Liefert true, wenn danach für (ruleId, field) nichts mehr offen ist. */
  decide(ruleId: string, field: RuleMapField, key: string, outcome: 'accepted' | 'rejected'): boolean {
    const state = this.states.get(this.id(ruleId, field));
    if (!state) return true;
    const index = state.open.indexOf(key);
    if (index >= 0) {
      state.open.splice(index, 1);
      if (outcome === 'accepted' && !state.accepted.includes(key)) {
        state.accepted.push(key);
      }
    }
    return state.open.length === 0;
  }

  acceptedKeys(ruleId: string, field: RuleMapField): string[] {
    return [...(this.states.get(this.id(ruleId, field))?.accepted ?? [])];
  }

  hasAccepted(): boolean {
    return [...this.states.values()].some(state => state.accepted.length > 0);
  }

  openCount(): number {
    return [...this.states.values()].reduce((sum, state) => sum + state.open.length, 0);
  }

  /** Vergisst alle Einträge einer Regel (offene und übernommene), z. B. wenn der Nutzer die Regel löscht. */
  forgetRule(ruleId: string): void {
    for (const field of ['parameterValues', 'tagValues'] as const) {
      this.states.delete(this.id(ruleId, field));
    }
  }

  reset(): void {
    this.states.clear();
  }

  private id(ruleId: string, field: RuleMapField): string {
    return `${ruleId}\u0000${field}`;
  }
}
